import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Popconfirm, Tag, Tooltip, Space, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import AssetModal from './AssetModal';
import { getAssets, createAsset, updateAsset, deleteAsset } from '../../services/api';
import toast from 'react-hot-toast';
import './AssetManagement.scss';
import  typeOptions  from '../utils/typeOptions';

const AssetManagement = ({ setTitle }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  useEffect(() => {
    setTitle('EarthVerse CRM - 素材管理');
    fetchAssets();
  }, [setTitle]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const fetchedAssets = await getAssets();
      setAssets(fetchedAssets);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to fetch assets. Please try again.');
      setLoading(false);
    }
  };

  const handleAddOrUpdateAsset = async (values) => {
    try {
      if (editingAsset) {
        await updateAsset(editingAsset.id, values);
        toast.success('Asset updated successfully!');
      } else {
        await createAsset(values);
        toast.success('Asset added successfully!');
      }
      setIsModalVisible(false);
      setEditingAsset(null);
      fetchAssets();
    } catch (error) {
      console.error('Error adding/updating asset:', error);
      toast.error('Failed to add/update asset. Please try again.');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await deleteAsset(assetId);
      toast.success('Asset deleted successfully!');
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error('Failed to delete asset. Please try again.');
    }
  };

  const showModal = (asset = null) => {
    setEditingAsset(asset);
    setIsModalVisible(true);
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 60,
      align: 'center',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (type) => (
        <Tag color="blue">
          {typeOptions.find((option) => option.value === type)?.label || type}
        </Tag>
      ),
    },
    {
      title: 'File Size (MB)',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (file_size) => file_size || '0.0',
    },
    {
      title: 'URL',
      dataIndex: 'file_uri',
      key: 'file_uri',
      width: 200, // 固定寬度
      ellipsis: true, // 自動省略超出部分
      render: (uri) =>
        uri ? (
          <Tooltip title={uri}>
            <span
              style={{ 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                display: 'block', 
                cursor: 'copy', // 更明確的複製光標
                color: '#1890ff', 
                textDecoration: 'underline', // 下劃線
              }}
              onClick={() => {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(uri).catch((err) => {
                    console.error('Failed to copy text:', err);
                  });
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = uri;
                  textarea.style.position = 'fixed'; // 防止頁面滾動
                  document.body.appendChild(textarea);
                  textarea.focus();
                  textarea.select();
                  try {
                    document.execCommand('copy');
                  } catch (err) {
                    console.error('Fallback: Unable to copy text', err);
                  }
                  document.body.removeChild(textarea);
                }
              }}
            >
              {uri}
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'File Name',
      dataIndex: 'uri',
      key: 'uri',
      width: 200, // 固定寬度
      render: (uri) => uri || '-',
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (image) => {
        if (!image) return '-';
        return isValidUrl(image) ? (
          <img src={image} alt="Asset" style={{ maxWidth: '50px', maxHeight: '50px' }} />
        ) : (
          <Tag
            color="green"
            style={{
              maxWidth: '150px', // 限制 Tag 最大寬度
              whiteSpace: 'nowrap', // 防止文字換行
              overflow: 'hidden', // 隱藏超出部分
              textOverflow: 'ellipsis', // 使用省略號
              display: 'inline-block', // 確保樣式生效
            }}
          >
            {image}
          </Tag>
        );
      },
    },
    {
      title: '到期時間',
      dataIndex: 'expire_time',
      key: 'expire_time',
      render: (date, record) =>
        record.is_live && date ? new Date(date).toLocaleString('zh-TW') : '-',
      sorter: (a, b) => 
        a.expire_time && b.expire_time ? 
        new Date(a.expire_time) - new Date(b.expire_time) : 0,
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (date) =>
        date ? new Date(date).toLocaleString('zh-TW') : '-',
      sorter: (a, b) => new Date(a.last_updated) - new Date(b.last_updated),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)} style={{ marginRight: 8 }} />
          <Popconfirm
            title="Are you sure you want to delete this asset?"
            onConfirm={() => handleDeleteAsset(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  const filteredAssets = assets.filter(asset =>
    (asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (asset.created_by && asset.created_by.toLowerCase().includes(searchTerm.toLowerCase()))) &&
    (selectedTypes.length === 0 || selectedTypes.includes(asset.type))
  );

  const TypeFilter = () => (
    <div style={{ marginBottom: 16 }}>
      <Checkbox.Group
        options={typeOptions}
        value={selectedTypes}
        onChange={setSelectedTypes}
      />
    </div>
  );

  return (
    <div className="asset-management-container">
      <h2>Asset管理</h2>
      <div className="asset-actions" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Search assets..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 200 }}
          />
          <TypeFilter />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Add New Asset
          </Button>
        </Space>
      </div>
      <Table
        dataSource={filteredAssets}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 30 }}
        loading={loading}
      />
      <AssetModal
        visible={isModalVisible}
        editingAsset={editingAsset}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingAsset(null);
        }}
        onSubmit={handleAddOrUpdateAsset}
      />
    </div>
  );
};

export default AssetManagement;
